/**
 * Created by florindavid on 19/12/2017.
 */
import { ReactElement, useEffect, useRef } from 'react'
import { IntlProvider } from 'react-intl'
import { connect } from 'react-redux'
import join from 'classnames'
import { ConfigProvider } from 'antd'

import log from 'lib/log'
import isAppleDevice from 'lib/is-apple-device'

import GenericError from './containers/GenericError'
import UpdateReadyBar from 'containers/UpdateReadyBar'

import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import Layout from './Layout'
import ProtectedRoute from 'components/ProtectedRoute'

import styles from './App.module.scss'

import {
  getGenericError,
  isOffline,
  isUpdateInProgress,
  isUpdateReady,
} from 'selectors/app'
import { getLocale, getMessages, getUpdatedAt } from 'selectors/intl'

import { FormattedMessage } from 'lib'

import { setOffline, setSmallScreenSize } from 'actions/app'
import { triggerSearch } from 'actions/search'
import { isDemoMode } from 'lib/is-demo'
import { getChartData } from 'selectors/search/chart'
import { isDemoLoading } from 'selectors/search/filters'
import { isUserLoggedIn } from 'selectors/auth'

import { SMALL_SCREEN } from 'constants/app'
import Search from 'screens/Search'
import Settings from 'screens/Settings'
import Login from 'screens/Login'

interface ReduxState {
  updateReady: boolean
  updateInProgress: boolean
  locale: string
  messages: Record<string, string>
  updatedAt: string
  offline: boolean
  error: GenericError | null
  isLoggedIn: boolean
}

const select = (): ReduxState => ({
  updateReady: isUpdateReady(),
  updateInProgress: isUpdateInProgress(),
  locale: getLocale(),
  messages: getMessages() as Record<string, string>,
  updatedAt: getUpdatedAt(),
  offline: isOffline(),
  error: getGenericError(),
  isLoggedIn: isUserLoggedIn(),
})

const router = createBrowserRouter([
  {
    path: '/login',
    Component: Login,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        Component: Search,
      },
      {
        path: '/settings',
        Component: Settings,
      },
    ],
  },
])

const App = (props: ReduxState): ReactElement => {
  const {
    locale,
    messages,
    updatedAt,
    offline,
    updateReady,
    updateInProgress,
    error,
    isLoggedIn,
  } = props

  const ref = useRef<HTMLDivElement | null>(null)

  const updateScreenSize = (): void => {
    if (!ref.current) {
      return
    } else {
      setSmallScreenSize(ref.current?.clientWidth < SMALL_SCREEN)
    }
  }

  useEffect(() => {
    if (!isDemoMode || !isLoggedIn) return

    // Trigger initial search (filter already added during login)
    const timer = setTimeout(() => triggerSearch(false), 1000)

    // Retry automatic search every 3s if LogEventsCount returned no data
    // and demo loading conditions are met (no custom period, only browserSession filter)
    const retryInterval = setInterval(() => {
      if (isDemoLoading() && getChartData().length === 0) {
        triggerSearch(false)
      }
    }, 3000)

    return () => {
      clearTimeout(timer)
      clearInterval(retryInterval)
    }
  }, [isLoggedIn])

  useEffect(() => {
    window.addEventListener('resize', updateScreenSize)
    window.addEventListener('orientationchange', updateScreenSize)
    return () => {
      window.addEventListener('resize', updateScreenSize)
      window.addEventListener('orientationchange', updateScreenSize)
    }
  }, [])

  useEffect(() => {
    const updateOnlineStatus = (): void => {
      log.info('Connection status changed', { online: navigator.onLine })
      setOffline(!navigator.onLine)
    }

    window.addEventListener('online', updateOnlineStatus)
    window.addEventListener('offline', updateOnlineStatus)

    // update now
    updateOnlineStatus()

    return () => {
      window.removeEventListener('online', updateOnlineStatus)
      window.removeEventListener('offline', updateOnlineStatus)
    }
  }, [])

  return (
    <IntlProvider key={updatedAt} locale={locale} messages={messages}>
      <ConfigProvider>
        <div
          className={join(
            styles.app,
            !isAppleDevice(navigator.platform) && styles.customScroll,
          )}
        >
          <RouterProvider router={router} />

          {error && <GenericError />}

          {updateInProgress && (
            <div className={styles.updating}>
              <FormattedMessage id={'updating'} />
            </div>
          )}

          {offline && <div className={styles.updating}>offline</div>}

          {updateReady && <UpdateReadyBar />}
        </div>
      </ConfigProvider>
    </IntlProvider>
  )
}

export default connect(select)(App)
